import React from 'react';
import { sendAlarm } from '../lib/whisper';

interface Props  { children: React.ReactNode; }
interface State  { hasError: boolean; err: Error | null; }

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, err: null };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, err };
  }

  componentDidCatch(err: Error, info: React.ErrorInfo): void {
    const componentStack = (info && info.componentStack) || '';
    // Fire an alarm to the Whisper pipe (red embed). Non-blocking.
    void sendAlarm(err, componentStack.trim().slice(0, 1200));
    // Also log locally so devs see it in console during development.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', err, info);
  }

  handleReload = (): void => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    const msg = (this.state.err && this.state.err.message) || 'Unknown error';

    return (
      <div style={{
        minHeight:       '100vh',
        backgroundColor: '#050816',
        color:           '#e8ecf5',
        fontFamily:      "'Inter', -apple-system, system-ui, sans-serif",
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         '32px',
      }}>
        <div style={{
          width:        '100%',
          maxWidth:     480,
          padding:      '32px 30px',
          border:       '1px solid rgba(220,38,38,0.35)',
          borderTop:    '2px solid #dc2626',
          borderRadius: 6,
          background:   'rgba(20, 10, 14, 0.55)',
          backdropFilter: 'blur(8px)',
        }}>
          <p style={{
            margin:        '0 0 10px',
            fontSize:      10,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color:         '#fca5a5',
            fontWeight:    600,
          }}>
            Oracle · Alarm
          </p>
          <h1 style={{
            margin:        '0 0 14px',
            fontFamily:    "'Merriweather', 'Georgia', serif",
            fontSize:      26,
            fontWeight:    700,
            letterSpacing: '-0.01em',
            color:         '#f4f6fb',
          }}>
            The signal dropped.
          </h1>
          <p style={{
            margin:    '0 0 18px',
            fontSize:  14,
            lineHeight: 1.7,
            color:     '#a8b1cb',
            fontWeight: 300,
          }}>
            Something unexpected interrupted the rite. A technical alert has been
            dispatched to the Architect. You can try again — the ledger is intact.
          </p>
          <pre style={{
            margin:    '0 0 18px',
            padding:   '10px 12px',
            fontFamily:'monospace',
            fontSize:  11,
            lineHeight: 1.5,
            color:     '#f87171',
            background:'#120609',
            border:    '1px solid #3f0a0a',
            borderRadius: 4,
            overflow:  'auto',
            maxHeight: 120,
          }}>
            {msg}
          </pre>
          <button
            onClick={this.handleReload}
            style={{
              width:         '100%',
              padding:       '11px 16px',
              background:    '#7c3aed',
              border:        'none',
              borderRadius:  3,
              color:         '#0b0418',
              fontFamily:    "'Inter', sans-serif",
              fontWeight:    600,
              fontSize:      11,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              cursor:        'pointer',
            }}>
            Reload
          </button>
          <p style={{
            margin:        '16px 0 0',
            fontSize:      9,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color:         '#475569',
            textAlign:     'center',
          }}>
            Architect: Dromide
          </p>
        </div>
      </div>
    );
  }
}
